/**
 * The status line of a sourcing run with a clock: how long the current step has been going and
 * roughly how long the profile reads still take (Sofia, Oct 19 2026 — a 2-minute check looked
 * like a hang). Mounted with `key={status.since}`, so the clock restarts with every new step.
 */
import { useEffect, useState } from 'react';

export type RunStatusValue = { text: string; problem: boolean; since: number; etaMs?: number };

const clock = (ms: number) => `${Math.floor(ms / 60_000)}:${String(Math.floor((ms % 60_000) / 1000)).padStart(2, '0')}`;

export function RunStatus({ status, running }: { status: RunStatusValue; running: boolean }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!running || status.problem) return;
    const t = setInterval(() => setElapsed(Date.now() - status.since), 1000);
    return () => clearInterval(t);
  }, [running, status.problem, status.since]);
  // Counts down with the clock (CodeRabbit, PR #629); never below "1 min" while the step still runs.
  const left = status.etaMs !== undefined ? Math.max(1, Math.round((status.etaMs - elapsed) / 60_000)) : null;
  return (
    <span className={`w-full text-xs ${status.problem ? 'text-[#E71D3F]' : 'text-[#0072EA]'}`}>
      {status.text}
      {running && !status.problem && (
        <span className="text-[#979797]">
          {' '}
          ({clock(elapsed)}
          {left ? ` · about ${left} min left` : ''})
        </span>
      )}
    </span>
  );
}
